import { useState, useRef, useCallback, useEffect } from "react";
import toast from "react-hot-toast";

import studioCycloramaImg from "../../assets/image/themes/studio-cyclorama.jpg";
import travertineArchImg from "../../assets/image/themes/travertine-arch.jpg";
import runwayCatwalkImg from "../../assets/image/themes/runway-catwalk.jpg";
import heritageCourtyardImg from "../../assets/image/themes/heritage-courtyard.jpg";
import luxuryAtelierImg from "../../assets/image/themes/luxury-atelier.jpg";
import regalCorridorImg from "../../assets/image/themes/regal-corridor.jpg";

import {
  generateAIBackground,
  getAIBgHistory,
  saveAIBgToLibrary,
  deleteAIBgHistory,
} from "../../api/aiGenerationService";

export const SAMPLE_THEMES = [
  {
    id: "studio-cyclorama",
    title: "Seamless Studio Cyclorama",
    tag: "Lookbook",
    prompt:
      "Professional high-fashion studio backdrop, seamless warm-white infinity cyclorama wall, soft directional overhead beauty dish lighting, subtle gradient floor reflection, crisp commercial apparel photoshoot aesthetic",
    image: studioCycloramaImg,
  },
  {
    id: "travertine-arch",
    title: "Minimalist Editorial Arch",
    tag: "Editorial",
    prompt:
      "Vogue editorial photoshoot backdrop, minimalist warm beige limestone archway, soft morning diffused sunlight casting delicate palm leaf shadows, smooth travertine floor for luxury garment showcase",
    image: travertineArchImg,
  },
  {
    id: "runway-catwalk",
    title: "Haute Couture Runway",
    tag: "Runway",
    prompt:
      "Paris haute couture runway catwalk backdrop, polished reflective black runway floor, dramatic overhead fashion spotlights, moody atmospheric lighting with soft bokeh background for high-fashion garments",
    image: runwayCatwalkImg,
  },
  {
    id: "heritage-courtyard",
    title: "Royal Heritage Courtyard",
    tag: "Heritage",
    prompt:
      "Majestic Rajasthani royal palace courtyard, intricate hand-carved sandstone jharokhas and scalloped arches, warm golden hour ambient sunlight, polished marble floor reflection, opulent backdrop for luxury ethnic couture and bridal lehenga photoshoot",
    image: heritageCourtyardImg,
  },
  {
    id: "luxury-atelier",
    title: "Minimalist Flagship Atelier",
    tag: "Atelier",
    prompt:
      "High-end luxury fashion flagship salon, fluted Italian marble pillars, diffused natural skylight, warm neutral micro-cement floor, sophisticated interior backdrop for designer tailoring and couture",
    image: luxuryAtelierImg,
  },
  {
    id: "regal-corridor",
    title: "Regal Mughal Corridor",
    tag: "Festive",
    prompt:
      "Opulent Indian heritage palace corridor with grand scalloped arches, glowing brass lanterns casting warm ambient light, intricate floral motif pillars, reflective polished stone floor, dramatic editorial backdrop for bridal lehengas and royal sherwanis",
    image: regalCorridorImg,
  },
];

const POLL_INTERVAL_MS = 7000; // 7 seconds as requested

const mapErrorCodeToMessage = (errorCode, rawError) => {
  switch (errorCode) {
    case "GPU_WORKER_TIMEOUT":
      return "Generation timed out on server. Please try again.";
    case "GENERATION_FAILED":
      return "Generation failed. Please refine your prompt and try again.";
    case "SERVER_RESTARTED":
      return "Server restarted during processing. Please resubmit your request.";
    case "INTERNAL_ERROR":
      return "Internal processing error occurred.";
    default:
      return rawError || "Generation failed. Please try again.";
  }
};

const BG_STAGES = [
  "Synthesizing...",
  "Rendering backdrop...",
  "Applying lighting...",
  "Finalizing...",
];

const getBackgroundStageText = (status) => {
  if (status === "queued") return "In queue...";
  if (status === "failed") return "Failed";
  if (status === "completed") return "Complete!";
  return BG_STAGES[0];
};

const getNextBgStage = (currentStage) => {
  const idx = BG_STAGES.indexOf(currentStage);
  if (idx === -1) return BG_STAGES[0];
  if (idx < BG_STAGES.length - 1) return BG_STAGES[idx + 1];
  return BG_STAGES[BG_STAGES.length - 1];
};

export const useBackgroundGenerator = ({ onAddBackgroundLocally } = {}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState({
    total: 0,
    queued: 0,
    generating: 0,
    completed: 0,
    failed: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState([]);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);

  const pollTimerRef = useRef(null);
  const smoothProgressIntervalRef = useRef(null);
  const lastClearedRef = useRef([]);
  const lastFilterRef = useRef({ page: 1, limit: 10, status: undefined, search: undefined });

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);
  const openHistory = () => setIsHistoryOpen(true);
  const closeHistory = () => setIsHistoryOpen(false);

  // Normalize backend job object for frontend consumers
  const normalizeJob = useCallback((j) => {
    const rawId = j.jobId || j.id;
    const promptText =
      j.prompt ||
      j.params?.prompt ||
      j.name ||
      "AI Generated Background";

    return {
      ...j,
      id: rawId,
      jobId: rawId,
      name: j.name || (promptText.length > 40 ? `${promptText.slice(0, 40)}...` : promptText),
      prompt: promptText,
      gender: j.gender || j.params?.gender || "unisex",
      status: j.status || "queued",
      progress: j.status === "completed" ? 100 : 0,
      stageText:
        j.status === "queued"
          ? "In queue..."
          : j.status === "completed"
          ? "Complete!"
          : j.status === "failed"
          ? "Failed"
          : (j.stageText && j.stageText !== "Initializing...")
          ? j.stageText
          : BG_STAGES[0],
      resultUrl: j.resultUrl || (Array.isArray(j.images) && j.images[0]) || null,
      error: j.error ? mapErrorCodeToMessage(j.errorCode, j.error) : null,
      rawError: j.error,
      errorCode: j.errorCode,
      createdAt: j.createdAt || new Date().toISOString(),
      savedToLibrary: Boolean(j.savedToLibrary),
      libraryId: j.libraryId || null,
    };
  }, []);

  // Fetch history and update queue and drafts
  const fetchHistory = useCallback(
    async ({ page = 1, limit = 10, status, search, silent = false } = {}) => {
      if (!silent) setIsFetchingHistory(true);
      lastFilterRef.current = { page, limit, status, search };
      try {
        const res = await getAIBgHistory({ page, limit, status, search });
        if (res.success && res.data) {
          const { rows = [], total = 0, totalPages = 1, stats = {}, filters: apiFilters = [] } = res.data;
          if (Array.isArray(apiFilters) && apiFilters.length > 0) {
            setFilters(apiFilters);
          }

          const normalizedRows = rows.map(normalizeJob);

          // Separate active queue jobs vs completed/failed history
          const activeJobs = normalizedRows.filter(
            (j) => j.status === "queued" || j.status === "generating"
          );
          const finishedRows = normalizedRows.filter(
            (j) => j.status === "completed" || j.status === "failed"
          );

          // Preserve any in-flight visual progress on active jobs
          setJobs((prevJobs) => {
            if (status && status !== "all" && status !== "generating" && status !== "queued") {
              if (stats.queued === 0 && stats.generating === 0) {
                return [];
              }
              return prevJobs;
            }
            return activeJobs.map((serverJob) => {
              const localMatch = prevJobs.find((p) => p.id === serverJob.id);
              if (localMatch && serverJob.status === "generating") {
                return {
                  ...serverJob,
                  stageText: localMatch.stageText || BG_STAGES[0],
                };
              }
              return serverJob;
            });
          });

          setHistory(finishedRows);
          setHistoryStats(stats);
          setPagination({ page, limit, total, totalPages });

          return { success: true, activeJobs, stats };
        }
        return { success: false };
      } catch (err) {
        console.error("Failed to fetch background generation history:", err);
        return { success: false };
      } finally {
        if (!silent) setIsFetchingHistory(false);
      }
    },
    [normalizeJob]
  );

  const fetchHistoryRef = useRef(fetchHistory);
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  });

  // Initial load
  useEffect(() => {
    fetchHistory({ page: 1, limit: 10 });
  }, [fetchHistory]);

  const hasGeneratingJobs = jobs.some((j) => j.status === "generating");
  const hasActiveQueue =
    hasGeneratingJobs ||
    jobs.some((j) => j.status === "queued") ||
    Boolean(historyStats && (historyStats.queued > 0 || historyStats.generating > 0));

  // Interpolated smooth progress simulation for active generating jobs between polls
  useEffect(() => {
    if (hasGeneratingJobs) {
      if (!smoothProgressIntervalRef.current) {
        smoothProgressIntervalRef.current = setInterval(() => {
          setJobs((prevJobs) =>
            prevJobs.map((j) => {
              if (j.status === "generating") {
                const nextStage = getNextBgStage(j.stageText);
                if (nextStage !== j.stageText) {
                  return {
                    ...j,
                    stageText: nextStage,
                  };
                }
              }
              return j;
            })
          );
        }, 4500);
      }
    } else {
      if (smoothProgressIntervalRef.current) {
        clearInterval(smoothProgressIntervalRef.current);
        smoothProgressIntervalRef.current = null;
      }
    }

    return () => {
      if (smoothProgressIntervalRef.current) {
        clearInterval(smoothProgressIntervalRef.current);
        smoothProgressIntervalRef.current = null;
      }
    };
  }, [hasGeneratingJobs]);

  // Master Polling Lifecycle (7-second timer)
  useEffect(() => {
    if (!hasActiveQueue) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    if (!pollTimerRef.current) {
      pollTimerRef.current = setInterval(async () => {
        const result = await fetchHistoryRef.current({
          ...lastFilterRef.current,
          silent: true,
        });

        if (result?.success) {
          const stillActive =
            (result.activeJobs && result.activeJobs.length > 0) ||
            Boolean(result.stats && (result.stats.queued > 0 || result.stats.generating > 0));

          if (!stillActive && pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
        }
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [hasActiveQueue]);

  // Submit background generation job
  const startGeneration = useCallback(
    async ({
      name,
      prompt,
      gender = "unisex",
      themeId,
      aspectRatio = "3:4",
      themeImage = null,
    }) => {
      const payload = {
        name: name?.trim(),
        prompt: prompt?.trim(),
        gender: gender || "unisex",
      };

      if (themeId) payload.themeId = themeId;
      if (aspectRatio) payload.aspectRatio = aspectRatio;

      const toastId = toast.loading("Submitting background generation...");

      const res = await generateAIBackground(payload);

      if (!res.success) {
        toast.error(res.message || "Failed to start generation", { id: toastId });
        return { success: false, message: res.message, status: res.status };
      }

      const { jobId, status = "generating" } = res.data;

      const newJob = normalizeJob({
        jobId,
        id: jobId,
        name: payload.name,
        prompt: payload.prompt,
        gender: payload.gender,
        status,
        progress: 0,
        stageText:
          status === "queued"
            ? "In queue..."
            : "Synthesizing...",
        themeImage,
        createdAt: new Date().toISOString(),
        params: payload,
      });

      setJobs((prev) => [newJob, ...prev.filter((j) => j.id !== jobId)]);

      toast.success(
        status === "queued"
          ? "Generation queued! Position tracking above."
          : "Generation started! Synthesizing backdrop...",
        { id: toastId, icon: "✨" }
      );

      // Trigger immediate background sync to kick off polling loop
      fetchHistory({ page: 1, limit: pagination.limit, silent: true });

      return { success: true, jobId, status };
    },
    [normalizeJob, pagination.limit, fetchHistory]
  );

  // Retry failed job
  const retryJob = useCallback(
    (jobId) => {
      const job =
        jobs.find((j) => j.id === jobId) ||
        history.find((h) => h.id === jobId);

      if (!job) return;

      const retryParams = job.params || {
        name: job.name,
        prompt: job.prompt,
        gender: job.gender,
        themeId: job.themeId,
      };

      startGeneration(retryParams);
    },
    [jobs, history, startGeneration]
  );

  // Cancel / dismiss active job card from client view
  const cancelJob = useCallback((jobId) => {
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
    toast("Job removed from active view.", { icon: "⏹️" });
  }, []);

  const dismissJob = useCallback((jobId) => {
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  // Save completed AI background to store library
  const saveToLibrary = useCallback(
    async (jobOrHistoryItem) => {
      if (!jobOrHistoryItem) return;
      const targetId = jobOrHistoryItem.id || jobOrHistoryItem.jobId;

      const toastId = toast.loading("Saving background to library...");

      const res = await saveAIBgToLibrary(targetId, {
        name: jobOrHistoryItem.name,
        gender: jobOrHistoryItem.gender || "unisex",
        tier: "AI Studio",
        bIsActive: true,
      });

      if (!res.success) {
        toast.error(res.message || "Failed to save to library", { id: toastId });
        return { success: false, message: res.message };
      }

      const savedData = res.data || {};

      // Notify parent listing hook if provided to refresh catalog immediately
      if (onAddBackgroundLocally) {
        onAddBackgroundLocally({
          id: savedData.id,
          name: savedData.name || jobOrHistoryItem.name,
          front: savedData.front || jobOrHistoryItem.resultUrl,
          image: savedData.front || jobOrHistoryItem.resultUrl,
          gender: savedData.gender || jobOrHistoryItem.gender,
          tier: "AI Studio",
          source: "ai",
          bIsActive: 1,
          isActive: 1,
        });
      }

      // Mark saved in local history state
      setHistory((prev) =>
        prev.map((h) =>
          h.id === targetId
            ? { ...h, savedToLibrary: true, libraryId: savedData.id }
            : h
        )
      );

      // Dismiss from live queue if still present
      setJobs((prev) => prev.filter((j) => j.id !== targetId));

      toast.success("Saved to your backgrounds library!", {
        id: toastId,
        icon: "🎉",
      });

      return { success: true, data: savedData };
    },
    [onAddBackgroundLocally]
  );

  // Delete history item
  const deleteHistoryItem = useCallback(
    async (id) => {
      const targetId = typeof id === "object" && id !== null ? (id.id || id.jobId) : id;

      // Guard against deleting generating job
      const targetJob = jobs.find((j) => j.id === targetId);
      if (targetJob && targetJob.status === "generating") {
        toast.error("Active generation cannot be deleted while in progress.");
        return;
      }

      // Identify the item being deleted to know its status
      const itemToDelete = history.find((h) => h.id === targetId) || targetJob;
      const deletedStatus = itemToDelete?.status || "completed";

      const toastId = toast.loading("Deleting generation draft...");
      const res = await deleteAIBgHistory(targetId);

      if (!res.success) {
        toast.error(res.message || "Failed to delete item", { id: toastId });
        return;
      }

      // Optimistically update history and jobs lists
      setHistory((prev) => prev.filter((item) => item.id !== targetId));
      setJobs((prev) => prev.filter((item) => item.id !== targetId));

      // Optimistically update stat counts for All, Completed, and other tabs
      setHistoryStats((prev) => {
        if (!prev) return prev;
        const statusKey =
          deletedStatus && prev[deletedStatus] !== undefined
            ? deletedStatus
            : "completed";
        return {
          ...prev,
          total: Math.max(0, (prev.total || 0) - 1),
          [statusKey]: Math.max(0, (prev[statusKey] || 0) - 1),
        };
      });

      // Optimistically update pagination total and totalPages
      setPagination((prev) => {
        if (!prev) return prev;
        const newTotal = Math.max(0, (prev.total || 0) - 1);
        const limit = prev.limit || 10;
        const newTotalPages = Math.max(1, Math.ceil(newTotal / limit));
        const newPage = Math.min(prev.page || 1, newTotalPages);
        return {
          ...prev,
          page: newPage,
          total: newTotal,
          totalPages: newTotalPages,
        };
      });

      // Re-fetch to synchronize canonical data and stats from server without UI flicker
      const currentFilter = lastFilterRef.current || {};
      const currentPage = currentFilter.page || pagination.page || 1;
      const currentLimit = currentFilter.limit || pagination.limit || 10;
      const remainingOnPage = history.filter((item) => item.id !== targetId);
      const targetPage =
        remainingOnPage.length === 0 && currentPage > 1 ? currentPage - 1 : currentPage;

      fetchHistory({
        ...currentFilter,
        page: targetPage,
        limit: currentLimit,
        silent: true,
      });

      toast.success("Draft removed from history", { id: toastId });
    },
    [jobs, history, pagination, fetchHistory]
  );

  const clearAllFinished = useCallback(() => {
    const finishedJobs = jobs.filter((j) => j.status !== "generating");
    if (finishedJobs.length === 0) return;

    lastClearedRef.current = finishedJobs;
    setJobs((prev) => prev.filter((j) => j.status === "generating"));

    toast("Active queue drafts cleared.", { icon: "📁" });
  }, [jobs]);

  const undoClear = useCallback(() => {
    if (lastClearedRef.current.length > 0) {
      setJobs((prev) => [...lastClearedRef.current, ...prev]);
      lastClearedRef.current = [];
      toast.success("Restored drafts to active queue!");
    }
  }, []);

  return {
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    isHistoryOpen,
    openHistory,
    closeHistory,
    genJobs: jobs,
    jobs,
    genHistory: history,
    history,
    historyStats,
    pagination,
    filters,
    isFetchingHistory,
    fetchHistory,
    startGeneration,
    retryJob,
    cancelJob,
    dismissJob,
    saveToLibrary,
    deleteHistoryItem,
    clearAllFinished,
    undoClear,
    hasClearedDrafts: lastClearedRef.current.length > 0,
  };
};
